import React from 'react';
import { Card } from '@components/common/card';
import {
  Users,
  Gauge,
  FileText,
  DollarSign,
  TrendingUp,
  TrendingDown,
} from 'lucide-react';
import { DashboardStats as DashboardStatsType } from '@/types/dashboard_types';

interface DashboardStatsProps {
  stats: DashboardStatsType;
  is_loading?: boolean;
}

export const DashboardStats: React.FC<DashboardStatsProps> = ({
  stats,
  is_loading = false
}) => {
  const format_currency = (amount: number) => {
    return `KES ${amount?.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })}`;
  };

  const calculate_percentage = (value: number, total: number): number => {
    if (total === 0) return 0;
    return Math.round((value / total) * 100);
  };

  // Safe access helper
  const safe_get = (obj: any, path: string, default_value: any = 0) => {
    return path.split('.').reduce((acc, part) => acc?.[part], obj) ?? default_value;
  };

  if (is_loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="bg-white rounded-lg border border-gray-200 p-6 animate-pulse">
            <div className="h-4 bg-gray-200 rounded w-1/2 mb-4"></div>
            <div className="h-8 bg-gray-200 rounded w-3/4 mb-2"></div>
            <div className="h-3 bg-gray-200 rounded w-full"></div>
          </div>
        ))}
      </div>
    );
  }

const stat_cards = [
    {
      title: 'Total Clients',
      value: safe_get(stats, 'clients.total_active', 0),
      icon: <Users size={24} />,
      color: 'bg-blue-500',
      details: [
        { label: 'Active', value: safe_get(stats, 'clients.total_active', 0), color: 'text-success' },
        { label: 'New This Month', value: safe_get(stats, 'clients.new_this_month', 0), color: 'text-purple-600' },
        { label: 'Total Meters', value: safe_get(stats, 'meters.total', 0), color: 'text-blue-600' }
      ],
      trend: {
        value: safe_get(stats, 'clients.new_this_month', 0),
        label: 'New this month'
      }
    },
    {
      title: 'Water Meters',
      value: safe_get(stats, 'meters.total', 0),
      icon: <Gauge size={24} />,
      color: 'bg-forest-green',
      details: [
        { label: 'Active', value: safe_get(stats, 'meters.active', 0), color: 'text-success' },
        { label: 'Faulty', value: safe_get(stats, 'meters.faulty', 0), color: 'text-error' },
        { label: 'Need Reading', value: safe_get(stats, 'meters.needing_reading', 0), color: 'text-warning' }
      ],
      trend: {
        value: calculate_percentage(
          safe_get(stats, 'meters.active', 0),
          safe_get(stats, 'meters.total', 0)
        ),
        label: 'Active rate'
      }
    },
    {
      title: 'Total Invoices',
      value: safe_get(stats, 'invoices.total', 0),
      icon: <FileText size={24} />,
      color: 'bg-purple-500',
      details: [
        { label: 'Draft', value: safe_get(stats, 'invoices.draft', 0), color: 'text-gray-600' },
        { label: 'Sent', value: safe_get(stats, 'invoices.sent', 0), color: 'text-blue-600' },
        { label: 'Overdue', value: safe_get(stats, 'invoices.overdue', 0), color: 'text-error' }
      ],
      trend: {
        value: safe_get(stats, 'invoices.outstanding_amount', 0) > 0 
          ? Math.round(safe_get(stats, 'invoices.outstanding_amount', 0))
          : 0,
        label: 'Outstanding (KES)'
      }
    },
    {
      title: 'Revenue This Month',
      value: format_currency(safe_get(stats, 'payments.this_month_revenue', 0)),
      icon: <DollarSign size={24} />,
      color: 'bg-success',
      details: [
        { label: 'Recent Payments', value: format_currency(safe_get(stats, 'payments.recent_payments_total', 0)), color: 'text-success' },
        { label: 'Collection Rate', value: `${safe_get(stats, 'payments.collection_rate', 0).toFixed(1)}%`, color: 'text-forest-green' },
        { label: 'Outstanding', value: format_currency(safe_get(stats, 'invoices.outstanding_amount', 0)), color: 'text-warning' }
      ],
      trend: {
        value: Math.round(safe_get(stats, 'payments.collection_rate', 0)),
        label: 'Collection rate'
      }
    }
  ];

  return (
    <div className="space-y-6">
      {/* Main Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {stat_cards.map((card, index) => (
          <Card key={index} padding="md" className="relative overflow-hidden">
            <div className="space-y-4">
              {/* Header */}
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm text-gray-500 font-medium">{card.title}</p>
                  <h3 className="text-2xl font-bold text-charcoal mt-1">
                    {typeof card.value === 'number' ? card.value.toLocaleString() : card.value}
                  </h3>
                </div>
                <div className={`w-12 h-12 rounded-lg ${card.color} text-white flex items-center justify-center`}>
                  {card.icon}
                </div>
              </div>

              {/* Details */}
              <div className="space-y-2">
                {card.details.map((detail, idx) => (
                  <div key={idx} className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">{detail.label}:</span>
                    <span className={`font-semibold ${detail.color}`}>
                      {typeof detail.value === 'number' ? detail.value.toLocaleString() : detail.value}
                    </span>
                  </div>
                ))}
              </div>

              {/* Trend */}
              <div className="pt-3 border-t border-gray-200 flex items-center justify-between">
                <span className="text-xs text-gray-500">{card.trend.label}</span>
                <div className="flex items-center gap-1">
                  {card.trend.value >= 0 ? (
                    <TrendingUp size={14} className="text-success" />
                  ) : (
                    <TrendingDown size={14} className="text-error" />
                  )}
                  <span className={`text-sm font-semibold ${
                    card.trend.value >= 0 ? 'text-success' : 'text-error'
                  }`}>
                    {Math.abs(card.trend.value)}%
                  </span>
                </div>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* Additional Stats Row */}
        {/* Water Consumption */}
        {/* <Card padding="md">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-lg bg-blue-100 text-blue-600 flex items-center justify-center flex-shrink-0">
              <Droplets size={24} />
            </div>
            <div className="flex-1">
              <p className="text-sm text-gray-500 font-medium">Water Consumption</p>
              <h3 className="text-2xl font-bold text-charcoal mt-1">
                {safe_get(stats, 'consumption.total_consumption', 0).toLocaleString()} m³
              </h3>
              <div className="mt-3 space-y-1 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Average:</span>
                  <span className="font-semibold text-charcoal">
                    {safe_get(stats, 'consumption.average_consumption', 0).toLocaleString()} m³
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Highest:</span>
                  <span className="font-semibold text-warning">
                    {safe_get(stats, 'consumption.highest_consumption', 0).toLocaleString()} m³
                  </span>
                </div>
              </div>
            </div>
          </div>
        </Card> */}

        {/* Invoice Status */}
        {/* <Card padding="md">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-lg bg-purple-100 text-purple-600 flex items-center justify-center flex-shrink-0">
              <FileText size={24} />
            </div>
            <div className="flex-1">
              <p className="text-sm text-gray-500 font-medium">Invoice Status</p>
              <h3 className="text-2xl font-bold text-charcoal mt-1">
                {safe_get(stats, 'invoices.total', 0).toLocaleString()}
              </h3>
              <div className="mt-3 space-y-1 text-sm">
                <div className="flex items-center gap-2">
                  <CheckCircle size={14} className="text-success" />
                  <span className="text-gray-600">Paid:</span>
                  <span className="font-semibold text-success">
                    {safe_get(stats, 'invoices.paid', 0)}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <AlertCircle size={14} className="text-error" />
                  <span className="text-gray-600">Overdue:</span>
                  <span className="font-semibold text-error">
                    {safe_get(stats, 'invoices.overdue', 0)}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock size={14} className="text-blue-600" />
                  <span className="text-gray-600">Sent:</span>
                  <span className="font-semibold text-blue-600">
                    {safe_get(stats, 'invoices.sent', 0)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </Card> */}

        {/* Collection Rate */}
        {/* <Card padding="md">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-lg bg-green-100 text-green-600 flex items-center justify-center flex-shrink-0">
              <DollarSign size={24} />
            </div>
            <div className="flex-1">
              <p className="text-sm text-gray-500 font-medium">Collection Rate</p>
              <h3 className="text-2xl font-bold text-success mt-1">
                {calculate_percentage(
                  safe_get(stats, 'payments.total_received', 0),
                  safe_get(stats, 'invoices.total_billed', 1)
                )}%
              </h3>
              <div className="mt-3">
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-success h-2 rounded-full transition-all"
                    style={{
                      width: `${calculate_percentage(
                        safe_get(stats, 'payments.total_received', 0),
                        safe_get(stats, 'invoices.total_billed', 1)
                      )}%`
                    }}
                  ></div>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  {format_currency(safe_get(stats, 'payments.total_received', 0))} of{' '}
                  {format_currency(safe_get(stats, 'invoices.total_billed', 0))} collected
                </p>
              </div>
            </div>
          </div>
        </Card> */}
    </div>
  );
};